import api, { API_BASE_URL } from './api'
import type {
  ColdContact,
  ColdContactImportResult,
  AvailableColdContact,
  ColdLetter,
  ColdLetterStats,
  TradeType,
} from '@/types'

// --- Cold Business (hopper) ---

export async function listColdContacts(
  territoryId: number,
  tradeType: TradeType,
): Promise<ColdContact[]> {
  const res = await api.get('/api/v1/cold-business/contacts', {
    params: { territory_id: territoryId, trade_type: tradeType },
  })
  return res.data
}

export async function importColdContacts(
  territoryId: number,
  tradeType: TradeType,
  file: File,
): Promise<ColdContactImportResult> {
  const form = new FormData()
  form.append('territory_id', String(territoryId))
  form.append('trade_type', tradeType)
  form.append('file', file)
  const res = await api.post('/api/v1/cold-business/contacts/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function deleteColdContact(contactId: number): Promise<void> {
  await api.delete(`/api/v1/cold-business/contacts/${contactId}`)
}

export function coldContactsTemplateUrl(): string {
  return `${API_BASE_URL}/api/v1/cold-business/contacts/template`
}

// --- Cold Letters ---

export async function getColdLetterStats(): Promise<ColdLetterStats> {
  const res = await api.get('/api/v1/cold-letters/stats')
  return res.data
}

export async function listAvailableColdContacts(
  clientId: number,
): Promise<AvailableColdContact[]> {
  const res = await api.get(`/api/v1/cold-letters/available-contacts/${clientId}`)
  return res.data
}

export async function listColdLettersByStatus(status: string): Promise<ColdLetter[]> {
  let endpoint = '/api/v1/cold-letters/'
  if (status === 'pending') endpoint = '/api/v1/cold-letters/pending'
  else if (status === 'approved') endpoint = '/api/v1/cold-letters/approved'
  else if (status === 'deleted') endpoint = '/api/v1/cold-letters/deleted'
  else if (status === 'all') endpoint = '/api/v1/cold-letters/'
  else endpoint = `/api/v1/cold-letters/?status=${status}`
  const res = await api.get(endpoint)
  return res.data
}

export async function listPendingColdLetters(): Promise<ColdLetter[]> {
  const res = await api.get('/api/v1/cold-letters/pending')
  return res.data
}

export async function getColdLetter(id: number): Promise<ColdLetter> {
  const res = await api.get(`/api/v1/cold-letters/${id}`)
  return res.data
}

export async function getColdLetterHistory(id: number): Promise<[]> {
  const res = await api.get(`/api/v1/cold-letters/${id}/history`)
  return res.data
}

export async function updateColdLetter(id: number, content: string, changeSummary?: string): Promise<ColdLetter> {
  const res = await api.put(`/api/v1/cold-letters/${id}`, {
    content,
    change_summary: changeSummary,
  })
  return res.data
}

export async function approveColdLetter(id: number): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/approve`)
  return res.data
}

export async function rejectColdLetter(id: number, reason?: string): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/reject`, { reason })
  return res.data
}

export async function regenerateColdLetter(id: number): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/regenerate`)
  return res.data
}

export async function deleteColdLetter(id: number, reason: string, deletionReason: string): Promise<void> {
  await api.delete(`/api/v1/cold-letters/${id}`, {
    data: { reason, deletion_reason: deletionReason },
  })
}

export async function restoreColdLetter(id: number): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/restore`)
  return res.data
}

export async function batchColdLetterPdf(letterIds: number[], markAsPrinted: boolean): Promise<Blob> {
  const res = await api.post(
    '/api/v1/cold-letters/batch-pdf',
    { letter_ids: letterIds, mark_as_printed: markAsPrinted, save_individual: true },
    { responseType: 'blob' },
  )
  return res.data
}

export async function listColdPrintQueue(): Promise<ColdLetter[]> {
  const res = await api.get('/api/v1/cold-letters/print-queue')
  return res.data
}

export async function markColdLetterPrinted(id: number): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/mark-printed`)
  return res.data
}

export async function markColdLetterMailed(id: number): Promise<ColdLetter> {
  const res = await api.post(`/api/v1/cold-letters/${id}/mark-mailed`)
  return res.data
}
